import { NewspaperOrder } from '../types/newspaperOrder';
import { getErrorReporter } from '../utils/errors';
import {
  NewspaperOrderTransaction,
  NewspaperOrderTransactionType,
  NewspaperOrderTransactionStatus
} from '../types/newspaperOrderTransaction';
import { BaseService } from './baseService';
import { EFirebaseContext, ERef, ETransaction } from '../types';
import { ColumnService } from './directory';
import {
  ResponseOrColumnError,
  wrapError,
  wrapSuccess
} from '../types/responses';
import { InternalServerError } from '../errors/ColumnErrors';

export class NewspaperOrderTransactionService extends BaseService<NewspaperOrderTransaction> {
  constructor(ctx: EFirebaseContext, newspaperOrderRef: ERef<NewspaperOrder>) {
    super(
      ctx,
      ctx.newspaperOrderNewspaperOrderTransactionsRef(newspaperOrderRef)
    );
  }

  async createPendingRefundTransaction(params: {
    convenienceFeeRefundPortionInCents: number;
    taxRefundPortionInCents: number;
    publisherRefundPortionInCents: number;
    applicationFeeRefundPortionInCents: number;
    reason: string | undefined;
    logData: Record<string, string>;
    options?: { transaction?: ETransaction };
  }): Promise<ResponseOrColumnError<ERef<NewspaperOrderTransaction>>> {
    const {
      convenienceFeeRefundPortionInCents,
      taxRefundPortionInCents,
      publisherRefundPortionInCents,
      applicationFeeRefundPortionInCents,
      reason,
      logData,
      options
    } = params;
    const newspaperOrderAmountInCents =
      convenienceFeeRefundPortionInCents +
      taxRefundPortionInCents +
      publisherRefundPortionInCents +
      applicationFeeRefundPortionInCents;
    if (newspaperOrderAmountInCents <= 0) {
      const err = new InternalServerError(
        'Refund amount must be positive (> 0)'
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        err,
        'Invalid refund amount to create pending refund transaction',
        logData
      );
      return wrapError(err);
    }

    const txData: NewspaperOrderTransaction = {
      newspaperOrderAmountInCents,
      publisherPortionInCents: publisherRefundPortionInCents,
      convenienceFeePortionInCents: convenienceFeeRefundPortionInCents,
      taxPortionInCents: taxRefundPortionInCents,
      applicationFeePortionInCents: applicationFeeRefundPortionInCents,
      type: NewspaperOrderTransactionType.REFUND,
      status: NewspaperOrderTransactionStatus.PENDING,
      ...(reason ? { reason } : {})
    };
    const [createErr, createdRef] = await this.create(txData, {
      transaction: options?.transaction
    });
    if (createErr) {
      return wrapError(createErr);
    }
    return wrapSuccess(createdRef);
  }
}
